import AppLayout from '@hellodarwin/app-core/lib/layouts/app-layout';
import { usePremiumAccess } from '@hellodarwin/app-core/lib/providers/premium-access-provider';
import {
  AppPage,
  MappedAppPages,
} from '@hellodarwin/core/lib/features/entities/pages-entities';
import AppRoutesRender from '@hellodarwin/core/lib/features/router/app-routes-render';
import { useEffect, useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';
import { useAppSelector } from './app/index';
import ClientCompleteProfileModal from './components/complete-profile-modal';
import LockedFeatureModal from './components/dashboard/features-block/components/locked-feature-modal';
import ProviderActionModal from './components/provider/provider-action-modal';
import RfpActionModal from './components/rfp/rfp-action-modal';
import {
  selectCheckoutLoading,
  selectProfileLoading,
} from './features/api/slices/profile-slice';
import { useUser } from './features/providers/user-provider';
import ClientAppHeader from './layout/client-app-header';
import ExpertAppHeader from './layout/expert-app-header';
import ExpertNavigation from './layout/expert-navigation';
import Navigation from './layout/navigation';
import OnboardingFlowWithProvider from './modules/onboarding-flow/components/OnboardingFlow';

import LoadingScreen from '@hellodarwin/core/lib/components/loading/screen';
import './App.scss';

const flattenPages = (mappedPages: MappedAppPages): AppPage[] =>
  Object.values(mappedPages).flatMap((page) => [
    page,
    ...flattenPages(page.children ?? {}),
  ]);

const ClientApp = () => {
  const { isLoggedIn, onboarding } = useUser();
  const { accessLevel, pages } = usePremiumAccess();
  const isExpert = accessLevel === 'expert';
  const hasResolvedShell = Object.keys(pages).length > 0;
  const { pathname, hash, key } = useLocation();
  const page = useMemo(
    () =>
      flattenPages(pages).find((candidatePage) =>
        matchPath(
          {
            path: candidatePage.pathname,
            end: true,
          },
          pathname,
        ),
      ),
    [pages, pathname],
  );

  const isHelloLoading = useAppSelector((state) =>
    selectProfileLoading(state, 'hello'),
  );
  const isCheckoutLoading = useAppSelector((state) =>
    selectCheckoutLoading(state),
  );
  const isAppLoading = useMemo(
    () =>
      isHelloLoading ||
      !hasResolvedShell ||
      (!isExpert && isCheckoutLoading) ||
      !isLoggedIn,
    [hasResolvedShell, isExpert, isHelloLoading, isCheckoutLoading, isLoggedIn],
  );

  useEffect(() => {
    document.body.classList.toggle('natural-scroll', !!page?.naturalScroll);

    return () => {
      document.body.classList.remove('natural-scroll');
    };
  }, [page?.naturalScroll]);

  //HASH
  useEffect(() => {
    // if not a hash link, scroll to top
    if (hash === '' && window !== undefined) {
      window.scrollTo(0, 0);
    }
    // else scroll to id
    else {
      setTimeout(() => {
        const id = hash.replace('#', '');
        const element = document.getElementById(id);
        if (element) {
          element.scrollIntoView();
        }
      }, 0);
    }
  }, [pathname, hash, key]);

  if (
    !isExpert &&
    onboarding !== 'none' &&
    !pathname.startsWith('/promo-checkout')
  ) {
    if (isAppLoading) return <LoadingScreen />;
    return <OnboardingFlowWithProvider />;
  }

  return (
    <>
      <AppLayout
        isLoading={isAppLoading}
        Navigation={
          page?.removeSidebar
            ? undefined
            : isExpert
              ? ExpertNavigation
              : Navigation
        }
        PageHeader={isExpert ? ExpertAppHeader : ClientAppHeader}
        naturalScroll={page?.naturalScroll}
      >
        <AppRoutesRender pages={pages} defaultHomepage={'/dashboard'} />
      </AppLayout>
      {isLoggedIn && hasResolvedShell && !isExpert && onboarding === 'none' && (
        <>
          <RfpActionModal />
          <ProviderActionModal />
          <ClientCompleteProfileModal />
          <LockedFeatureModal />
        </>
      )}
    </>
  );
};

export default ClientApp;
